import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Heart } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';
import { copy } from '@/copy';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const RecoveryPage = () => {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error(copy.recovery.emailRequired);
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/api/auth/recover`, { email, type: 'password' });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F5F5F5] flex flex-col">
      <header className="px-4 py-6 border-b border-[#E5E5E5]">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Link to="/" className="font-serif text-lg text-[#1D2D44]">
            Our Love <Heart className="inline-block w-4 h-4 text-[#C9A84C] mx-0.5" strokeWidth={1.5} /> Connection
          </Link>
          <Link to="/login" className="text-sm text-[#424E59] hover:text-[#1D2D44] transition-colors" data-testid="back-to-login-link">
            {copy.recovery.backToLogin}
          </Link>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center px-4 py-12">
        <Card className="w-full max-w-md border-none shadow-lg rounded-2xl overflow-hidden" data-testid="recovery-card">
          <div className="h-1 bg-[#B89B5E]"></div>
          <CardContent className="p-8">
            {submitted ? (
              <div className="text-center animate-fade-in" data-testid="recovery-success">
                <div className="w-12 h-12 bg-[#5E7F63]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Heart className="w-6 h-6 text-[#5E7F63]" strokeWidth={1.5} />
                </div>
                <h2 className="font-serif text-2xl text-[#1D2D44] mb-3">{copy.recovery.sentTitle}</h2>
                <p className="text-[#3A4A5A] text-sm leading-relaxed mb-6">
                  {copy.recovery.sentMessage}
                </p>
                <Link to="/login">
                  <Button className="bg-[#1D2D44] text-[#B89B5E] hover:bg-[#1F1F1F] rounded-full px-8" data-testid="return-to-login-btn">
                    {copy.recovery.returnToLogin}
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="animate-fade-in">
                <h2 className="font-serif text-2xl text-[#1D2D44] mb-2">{copy.recovery.title}</h2>
                <p className="text-[#3A4A5A] text-sm mb-6">{copy.recovery.subtitle}</p>

                <form onSubmit={handleSubmit}>
                  <div className="mb-5">
                    <label className="block text-sm font-medium text-[#1D2D44] mb-1.5">{copy.recovery.emailLabel}</label>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder={copy.recovery.emailPlaceholder}
                      required
                      className="w-full px-4 py-3 rounded-xl border border-[#E5E5E5] bg-white text-[#1D2D44] placeholder:text-[#B5ADA7] focus:outline-none focus:ring-2 focus:ring-[#B89B5E]/40 focus:border-[#B89B5E] transition-all"
                      data-testid="recovery-email-input"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-[#1D2D44] text-[#B89B5E] hover:bg-[#1F1F1F] rounded-full py-6 text-base font-medium transition-all"
                    data-testid="send-recovery-btn"
                  >
                    {loading ? (
                      <span className="flex items-center justify-center">
                        <span className="w-4 h-4 border-2 border-[#B89B5E] border-t-transparent rounded-full animate-spin mr-2"></span>
                        {copy.recovery.sending}
                      </span>
                    ) : (
                      copy.recovery.sendButton
                    )}
                  </Button>
                </form>

                <p className="text-center text-xs text-[#424E59] mt-5">{copy.recovery.securityNote}</p>
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default RecoveryPage;
